import ModalMotivoFalta from '../modals/ModalFrequencia';
import MyContext from '@/shared/contexts/AppContext';
import { AttendanceSheetDayEnum } from '../../enums/attendance-sheet-day.enum';
import { AttendanceSheetResponseDto, AttendanceSheetDayDto } from '@/api.types';
import { CiClock1 } from "react-icons/ci";
import { LuTreePalm } from 'react-icons/lu';
import { format, isWeekend } from 'date-fns';
import { useState, useEffect, useContext } from 'react';

type AttendanceCalendarProps = {
  folha: AttendanceSheetResponseDto;
  updateCalendarDay: (modifiedDay: AttendanceSheetDayDto) => void;
  horarioPadraoEntrada: string;
  horarioPadraoSaida: string;
  isWeekendWorker: boolean;
  cargaHorariaMaxima?: number;
};

const AttendanceCalendar = ({
  folha,
  updateCalendarDay,
  horarioPadraoEntrada,
  horarioPadraoSaida,
  isWeekendWorker: ehTrabalhadorFimDeSemana,
  cargaHorariaMaxima = 300,
}: AttendanceCalendarProps) => {
  const [modalEditarDiaAberto, setModalAberto] = useState(false);

  const [diaSelecionado, setDiaSelecionado] = useState<AttendanceSheetDayDto>();
  const context = useContext(MyContext);

  const [attendanceSheetDays, setAttendanceSheetDays] = useState<AttendanceSheetDayDto[]>(folha.dias);

  const handleChangeStatus = (dayIndex: number, newStatus: AttendanceSheetDayEnum) => {
    const updatedDays = [...attendanceSheetDays];
    const dia = updatedDays[dayIndex];
    dia.status = newStatus as unknown as AttendanceSheetDayDto["status"];

    if (newStatus === AttendanceSheetDayEnum.PRESENTE && (!dia.horarioEntrada || !dia.horarioSaida)) {
      dia.horarioEntrada = horarioPadraoEntrada;
      dia.horarioSaida = horarioPadraoSaida;
    }
    setAttendanceSheetDays(updatedDays);
  };

  function adjustFirstDayPosition() {
    const primeiroDia = new Date(folha.ano, folha.mes - 1, 1).getDay();
    return Array(primeiroDia).fill(null);
  }

  function renderStatusIcon(status: AttendanceSheetDayEnum) {
    switch (status) {
      case AttendanceSheetDayEnum.PRESENTE:
        return <span className="icone icone-presente">✓</span>;
      case AttendanceSheetDayEnum.FALTA_JUSTIFICADA:
      case AttendanceSheetDayEnum.FALTA_NAO_JUSTIFICADA:
        return <span className="icone icone-falta">✗</span>;
      case AttendanceSheetDayEnum.FERIAS:
        return <span className="icone-vacation"><LuTreePalm /></span>;
      case AttendanceSheetDayEnum.PENDENTE:
        return <span className="icone-cone-pendente"><CiClock1 /></span>;
      case AttendanceSheetDayEnum.FERIADO:
        return <span className="icone icone-feriado"></span>;
      default:
        return <span className="icone icone-neutro">-</span>;
    }
  }

  useEffect(() => {
    setAttendanceSheetDays(folha.dias);
  }, [folha.dias]);

  return (
    <>
      <div className="dias-mes">
        {adjustFirstDayPosition().map((_, index) => (
          <div key={`empty-${index}`} className="dia vazio" />
        ))}

        {attendanceSheetDays.map((registro, index) => {
          const date = new Date(folha.ano, folha.mes - 1, registro.dia);
          const ehFinalDeSemana = isWeekend(date);
          const ehEstagiario = context?.state.cpf === folha.estagiario?.cpf;

          const ehDesabilitado = (ehEstagiario ? (ehTrabalhadorFimDeSemana ? !ehFinalDeSemana : ehFinalDeSemana || !registro.util) : false);

          let classes = ''
          if (folha.estagio.lotacao?.diaslivres) { }
          else if (registro.feriado || ehDesabilitado || (ehTrabalhadorFimDeSemana && !ehFinalDeSemana) || (!ehTrabalhadorFimDeSemana && ehFinalDeSemana)) {
            classes = 'final-semana'
          }

          let tooltip = registro.feriado
            ? `${format(date, 'dd')} ${registro.feriado}`
            : `${format(date, 'dd')} - ${ehTrabalhadorFimDeSemana
              ? (ehFinalDeSemana ? 'Dia de trabalho' : 'Folga')
              : (ehFinalDeSemana ? 'Folga' : 'Dia de trabalho')}`;

          if (registro.justificativa)
            tooltip = tooltip.concat(` (${registro.justificativa})`)

          return (
            <div
              key={format(date, 'yyyy-MM-dd')}
              className={`dia ${classes}`}
              title={tooltip}
              onClick={() => {
                if (ehDesabilitado && !folha.estagio.lotacao?.diaslivres)
                  return;

                const dia = attendanceSheetDays[index];

                if (ehEstagiario &&
                  dia.status === Number(AttendanceSheetDayEnum.NAO_PREENCHIDO) &&
                  ((ehTrabalhadorFimDeSemana && ehFinalDeSemana) || (!ehTrabalhadorFimDeSemana && registro.util)) &&
                  !dia.feriado
                ) {
                  handleChangeStatus(index, AttendanceSheetDayEnum.PRESENTE);
                }

                setDiaSelecionado(dia);
                setModalAberto(true);
              }}
            >
              <div className="numero-dia">
                {format(date, 'dd')}{' '}
                {registro.temAnexo && <span title="Possui anexo.">📎</span>}
                {registro.historico && registro.historico?.length > 1 && <span title='Editado várias vezes.'>✐</span>}
              </div>
              <div className="botoes mt-0">
                {renderStatusIcon(registro.status)}
              </div>

              {registro.status === AttendanceSheetDayEnum.PRESENTE &&
                registro.horarioEntrada &&
                registro.horarioSaida && (
                  <div className="horarios">
                    {registro.horarioEntrada.substring(0, 5)} -{' '}
                    {registro.horarioSaida.substring(0, 5)}
                  </div>
                )}
            </div>
          );
        })}
      </div>

      {modalEditarDiaAberto && diaSelecionado && (
        <ModalMotivoFalta
          modifiedDay={diaSelecionado}
          folha={folha}
          isOpen={modalEditarDiaAberto}
          onConfirm={(modified) => {
            updateCalendarDay(modified);
            setModalAberto(false);
          }}
          onCancel={() => setModalAberto(false)}
          cargaHorariaMaxima={cargaHorariaMaxima}
        />
      )}
    </>
  );
};

export default AttendanceCalendar;
